import React, { useEffect, useState } from "react";
import { Card, Grid, Tab, Tabs, Chip } from "@mui/material";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { useDispatch, useSelector } from "react-redux";
import { iqmsActions } from "store/iqms-slice";
import { handleAxiosError } from "app/services/app.error.handler";
import {
  fetchAllProcesses,
  fetchAllAudits,
  fetchAllCapa,
  fetchAllRisks,
} from "app/services/iqms.service";
import { ProcessDataGrid } from "./ProcessDataGrid";
import { AuditDataGrid } from "./AuditDataGrid";
import { CapaDataGrid } from "./CapaDataGrid";
import { RiskDataGrid } from "./RiskDataGrid";

export const IqmsManagerView = () => {
  const dispatch = useDispatch();
  const [tabValue, setTabValue] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const processes = useSelector((state) => state.iqms.processes);
  const audits = useSelector((state) => state.iqms.audits);
  const capas = useSelector((state) => state.iqms.capas);
  const risks = useSelector((state) => state.iqms.risks);

  const loadData = () => {
    setIsLoading(true);
    Promise.all([
      fetchAllProcesses().then((data) => dispatch(iqmsActions.setProcesses(data))),
      fetchAllAudits().then((data) => dispatch(iqmsActions.setAudits(data))),
      fetchAllCapa().then((data) => dispatch(iqmsActions.setCapas(data))),
      fetchAllRisks().then((data) => dispatch(iqmsActions.setRisks(data))),
    ])
      .catch((error) => handleAxiosError(error))
      .finally(() => setIsLoading(false));
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scheduledAudits = audits.filter((a) => a.status === "SCHEDULED");
  const openCapas = capas.filter((c) => c.status === "OPEN" || c.status === "IN_PROGRESS");
  const highRisks = risks.filter((r) => r.likelihood === "HIGH" || r.likelihood === "CRITICAL" || r.impact === "HIGH" || r.impact === "CRITICAL");

  return (
    <MDBox py={3}>
      <Grid container spacing={3}>
        {/* Stats Cards */}
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="info">Active Processes</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">
                {processes.filter((p) => p.status === "ACTIVE").length}
              </MDTypography>
              <MDTypography variant="caption">In Use</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="success">Upcoming Audits</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{scheduledAudits.length}</MDTypography>
              <MDTypography variant="caption">Scheduled</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="warning">Open CAPAs</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{openCapas.length}</MDTypography>
              <MDTypography variant="caption">Requiring Action</MDTypography>
            </MDBox>
          </Card>
        </Grid>
        <Grid item xs={12} md={6} lg={3}>
          <Card>
            <MDBox p={3} textAlign="center">
              <MDTypography variant="h6" color="error">High Risks</MDTypography>
              <MDTypography variant="h3" fontWeight="bold">{highRisks.length}</MDTypography>
              <MDTypography variant="caption">Need Attention</MDTypography>
            </MDBox>
          </Card>
        </Grid>

        {/* Main Content with Tabs */}
        <Grid item xs={12}>
          <Card>
            <MDBox p={2}>
              <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
                <Tab label="Processes" />
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  Audits
                  {scheduledAudits.length > 0 && <Chip size="small" label={scheduledAudits.length} color="info" />}
                </MDBox>} />
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  CAPA
                  {openCapas.length > 0 && <Chip size="small" label={openCapas.length} color="warning" />}
                </MDBox>} />
                <Tab label={<MDBox display="flex" alignItems="center" gap={1}>
                  Risks
                  {highRisks.length > 0 && <Chip size="small" label={highRisks.length} color="error" />}
                </MDBox>} />
              </Tabs>
            </MDBox>
            {tabValue === 0 && (
              <ProcessDataGrid processes={processes} isLoading={isLoading} showActions={false} />
            )}
            {tabValue === 1 && (
              <AuditDataGrid audits={audits} isLoading={isLoading} showActions={false} />
            )}
            {tabValue === 2 && (
              <CapaDataGrid capas={capas} isLoading={isLoading} showActions={false} />
            )}
            {tabValue === 3 && (
              <RiskDataGrid risks={risks} isLoading={isLoading} showActions={false} />
            )}
          </Card>
        </Grid>
      </Grid>
    </MDBox>
  );
};

